import { createSelector } from '@reduxjs/toolkit';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'preact/compat';
import { useEffect } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { postUserAppOnboardedMutationFn } from '@/queries/users';
import { useSelector, useStore } from '@/redux/helper';
import { createSortedHabitatsSelector } from '@/redux/selectors/habitats';
import { selectActiveProductNames } from '@/redux/selectors/users';
import { updateUserProperty } from '@/redux/slices/user/actions';
import { trackMP } from '@/shared/mp';

import ExplorerDayPass from '../products/ExplorerDayPass';
import SingleHabitatDayPass from '../products/SingleHabitatDayPass';

const selectHabitat = createSelector(
  createSortedHabitatsSelector(['online', 'lastMediaCapture', 'index', 'currentViewers', 'title']),
  (habitats) => habitats?.[0],
);

const AppOnboardingProductsOverview = () => {
  const dispatch = useDispatch();
  const store = useStore();
  const isEducator = useSelector((state) => state.user.persona === 'educator');
  const [habitat, setHabitat] = useState(() => selectHabitat(store.getState()));
  const activeProductNames = useSelector(selectActiveProductNames);
  const hasActiveProduct = activeProductNames.length > 0;

  const { mutate: markOnboarded } = useMutation({
    mutationFn: postUserAppOnboardedMutationFn,
    onSuccess: (data) => {
      dispatch(updateUserProperty(data));
    },
  });

  const finishOnboarding = () => {
    markOnboarded();
    trackMP('after-signup-step', { step: 'productsOverview', activeProductNames });
  };

  // only update the cached habitats if there are no habitats in the cache
  useEffect(() => {
    if (habitat) {
      return undefined;
    }
    const unsubscribe = store.subscribe(() => {
      setHabitat(selectHabitat(store.getState()));
    });
    return () => unsubscribe();
  }, [store, habitat]);

  // skip onboarding if user has explorer feature already, usually for bundling users
  useEffect(() => {
    if (hasActiveProduct) {
      markOnboarded();
    }
  }, [hasActiveProduct, markOnboarded]);

  if (hasActiveProduct) {
    return null;
  }

  return (
    <div className="flex h-full flex-col">
      <div className="shrink-0">
        <h1 className="preflight preflight-h1 p-5 pb-4 pt-10 text-center text-2xl/7 font-bold text-[#162B1B]">
          How Do You Want to Visit?
        </h1>
      </div>
      <div className="min-h-0 grow overflow-y-auto px-8 pb-8">
        <div className="flex flex-col items-stretch justify-center gap-8 pt-4">
          <ExplorerDayPass variant="primary" forEducator={isEducator} entryPoint="onboarding" />
          {habitat && <SingleHabitatDayPass habitatId={habitat._id} />}
          <button
            type="button"
            className="preflight preflight-button text-grey-6 underline"
            onClick={() => finishOnboarding()}
          >
            Choose Another Animal
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppOnboardingProductsOverview;
